import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api-client';

const inputCls = `w-full bg-sand/40 border border-cream text-charcoal rounded-lg
  px-3 py-2 text-sm font-sans placeholder:text-stone
  hover:bg-sand/60 focus:outline-none focus:border-terracotta focus:shadow-focus-blue
  transition-all duration-normal`;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen bg-parchment flex items-center justify-center p-4 light-surface">
        <div className="w-full max-w-sm text-center">
          <Logo />
          <div className="bg-ivory border border-cream rounded-xl shadow-whisper p-6">
            <p className="font-sans text-sm text-red-700 mb-4">Invalid or missing reset token.</p>
            <button onClick={() => navigate('/login')}
              className="font-sans text-sm text-terracotta hover:underline font-medium">
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setError('');
    setLoading(true);
    try {
      await api.post('/api/auth/change-password/confirm', { token, newPassword: password });
      setDone(true);
      setTimeout(() => navigate('/login?password_changed=1'), 2000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to reset password.';
      setError(msg.includes('expired') || msg.includes('invalid')
        ? 'This reset link has expired or is invalid. Please request a new one.'
        : msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-parchment flex items-center justify-center p-4 light-surface">
      <div className="w-full max-w-sm">
        <Logo />
        <div className="bg-ivory border border-cream rounded-xl shadow-whisper">
          <div className="p-6">
            <h2 className="font-serif text-xl font-medium text-charcoal mb-1" style={{ lineHeight: 1.2 }}>
              Set new password
            </h2>
            <p className="font-sans text-sm text-olive mb-5" style={{ lineHeight: 1.6 }}>
              Choose a strong password for your account.
            </p>

            {done ? (
              <div className="flex flex-col items-center gap-3 py-6">
                <div className="w-12 h-12 rounded-full bg-green-50 border border-green-200 flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="font-sans text-sm font-medium text-charcoal">Password updated!</p>
                <p className="font-sans text-xs text-stone">Redirecting to sign in…</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block font-sans text-xs font-medium text-olive mb-1.5">New password</label>
                  <input className={inputCls} type="password" placeholder="Min 8 characters"
                    value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} autoFocus />
                </div>
                <div>
                  <label className="block font-sans text-xs font-medium text-olive mb-1.5">Confirm password</label>
                  <input className={inputCls} type="password" placeholder="Repeat password"
                    value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 font-sans text-sm text-red-700">
                    {error}
                  </div>
                )}

                <button type="submit" disabled={loading}
                  className="w-full bg-terracotta text-ivory font-sans font-medium text-sm rounded-lg py-2.5
                             shadow-ring-terracotta hover:bg-terracotta-light
                             transition-all duration-normal disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading ? 'Updating…' : 'Update password'}
                </button>
              </form>
            )}
          </div>

          <div className="px-6 pb-5 text-center border-t border-cream pt-4">
            <button onClick={() => navigate('/login')}
              className="font-sans text-xs text-stone hover:text-olive transition-colors">
              ← Back to sign in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Logo() {
  return (
    <div className="text-center mb-10">
      <img src="/icon-512.png" alt="Spine Workspace" className="w-16 h-16 rounded-2xl mx-auto mb-5 shadow-whisper" />
      <h1 className="font-serif text-3xl font-medium text-charcoal" style={{ lineHeight: 1.1 }}>
        Spine Workspace
      </h1>
      <p className="font-sans text-sm text-stone mt-1.5">Eighth Era Studios</p>
    </div>
  );
}
